import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Download, Paperclip, FileText, Shield, Truck, Calendar, Fuel, Gauge, Eye, Image as ImageIcon } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { vehicleService } from '../../services/vehicle.service';
import { vehicleExpenseService } from '../../services/vehicleExpense.service';
import { ExpenseCategory, ExpenseStatus, VehicleExpense } from '../../types';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Badge } from '../../components/ui/Badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/Table';
import { AttachmentViewer } from '../../components/ui/AttachmentViewer';
import toast from 'react-hot-toast';

export const VehicleDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  // Expense modal states
  const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
  const [expenseType, setExpenseType] = useState<'LATRA' | 'INSURANCE'>('LATRA');
  const [expenseAmount, setExpenseAmount] = useState('');
  const [expenseDate, setExpenseDate] = useState(new Date().toISOString().split('T')[0]);
  const [validFrom, setValidFrom] = useState(new Date().toISOString().split('T')[0]);
  const [validUntil, setValidUntil] = useState('');
  const [policyNumber, setPolicyNumber] = useState('');
  const [insuranceProvider, setInsuranceProvider] = useState('');
  const [expenseDescription, setExpenseDescription] = useState('');
  const [expenseAttachment, setExpenseAttachment] = useState<File | null>(null);
  
  // Attachment viewer state
  const [isAttachmentViewerOpen, setIsAttachmentViewerOpen] = useState(false);
  const [viewingAttachmentExpense, setViewingAttachmentExpense] = useState<VehicleExpense | null>(null);

  const { data: vehicle, isLoading: isLoadingVehicle } = useQuery({
    queryKey: ['vehicle', id],
    queryFn: () => vehicleService.getById(Number(id)),
    enabled: !!id,
  });

  const { data: expenses } = useQuery({
    queryKey: ['vehicleExpenses', id],
    queryFn: () => vehicleExpenseService.getByVehicle(Number(id)),
    enabled: !!id,
  });

  const createExpenseMutation = useMutation({
    mutationFn: vehicleExpenseService.recordExpense,
    onSuccess: async (createdExpense) => {
      if (expenseAttachment) {
        try {
          await vehicleExpenseService.uploadAttachment(createdExpense.id, expenseAttachment);
          toast.success(`${expenseType} expense recorded with attachment`);
        } catch (err) {
          toast.success(`${expenseType} expense recorded (attachment upload failed)`);
        }
      } else {
        toast.success(`${expenseType} expense recorded successfully`);
      }
      queryClient.invalidateQueries({ queryKey: ['vehicleExpenses', id] });
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      handleCloseExpenseModal();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to record expense');
    },
  });

  const handleCloseExpenseModal = () => {
    setIsExpenseModalOpen(false);
    setExpenseAmount('');
    setExpenseDate(new Date().toISOString().split('T')[0]);
    setValidFrom(new Date().toISOString().split('T')[0]);
    setValidUntil('');
    setPolicyNumber('');
    setInsuranceProvider('');
    setExpenseDescription('');
    setExpenseAttachment(null);
  };

  const handleOpenExpenseModal = (type: 'LATRA' | 'INSURANCE') => {
    setExpenseType(type);
    setIsExpenseModalOpen(true);
  };

  const handleSubmitExpense = () => {
    if (!expenseAmount || !validFrom || !validUntil) {
      toast.error('Please fill in all required fields');
      return;
    }

    createExpenseMutation.mutate({
      vehicleId: Number(id),
      expenseDate,
      category: expenseType === 'LATRA' ? ExpenseCategory.LATRA : ExpenseCategory.INSURANCE,
      amount: parseFloat(expenseAmount),
      validFrom,
      validUntil,
      policyNumber: policyNumber || undefined,
      insuranceProvider: insuranceProvider || undefined,
      description: expenseDescription || `${expenseType} payment`,
    });
  };

  const handleDownloadAttachment = async (expense: VehicleExpense) => {
    try {
      const blob = await vehicleExpenseService.downloadAttachment(expense.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = expense.attachmentFileName || 'attachment';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      toast.error('Failed to download attachment');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-TZ', {
      style: 'currency',
      currency: 'TZS',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusVariant = (status: ExpenseStatus) => {
    switch (status) {
      case ExpenseStatus.APPROVED: return 'success';
      case ExpenseStatus.PENDING: return 'warning';
      case ExpenseStatus.REJECTED: return 'error';
      case ExpenseStatus.PAID: return 'info';
      default: return 'default';
    }
  };

  const getCategoryLabel = (category: ExpenseCategory) => {
    const labels: Record<string, string> = {
      FUEL: 'Fuel',
      MAINTENANCE: 'Maintenance',
      REPAIR: 'Repair',
      INSURANCE: 'Insurance',
      LATRA: 'LATRA',
      REGISTRATION: 'Registration',
      PARKING: 'Parking',
      TOLLS: 'Tolls',
      CLEANING: 'Cleaning',
      TIRES: 'Tires',
      PARTS: 'Parts',
      LABOR: 'Labor',
      INSPECTION: 'Inspection',
      FINES: 'Fines',
      DRIVER_ALLOWANCE: 'Driver Allowance',
      ACCOMMODATION: 'Accommodation',
      MEALS: 'Meals',
      TOLL_ROAD: 'Toll/Road',
      LOADING_OFFLOADING: 'Loading/Offloading',
      WEIGHBRIDGE: 'Weighbridge',
      BORDER_FEES: 'Border Fees',
      FUEL_ADVANCE: 'Fuel Advance',
      OTHER: 'Other',
    };
    return labels[category] || category;
  };

  if (isLoadingVehicle) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Loading vehicle details...</p>
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <p className="text-gray-500 mb-4">Vehicle not found</p>
        <Button onClick={() => navigate('/vehicles')}>Back to Vehicles</Button>
      </div>
    );
  }

  // Separate expenses by category
  const latraExpenses = expenses?.filter(e => e.category === ExpenseCategory.LATRA) || [];
  const insuranceExpenses = expenses?.filter(e => e.category === ExpenseCategory.INSURANCE) || [];
  const maintenanceExpenses = expenses?.filter(e => 
    e.category === ExpenseCategory.MAINTENANCE || e.category === ExpenseCategory.REPAIR
  ) || [];
  const otherExpenses = expenses?.filter(e => 
    e.category !== ExpenseCategory.LATRA && 
    e.category !== ExpenseCategory.INSURANCE &&
    e.category !== ExpenseCategory.MAINTENANCE &&
    e.category !== ExpenseCategory.REPAIR
  ) || [];

  // Calculate totals
  const totalExpenses = expenses?.reduce((sum, e) => sum + e.amount, 0) || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/vehicles')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-neutral-charcoal">
              {vehicle.registrationNumber}
            </h1>
            <p className="text-gray-600">{vehicle.make} {vehicle.model} ({vehicle.year})</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => handleOpenExpenseModal('LATRA')}>
            <FileText size={16} className="mr-2" />
            Add LATRA
          </Button>
          <Button variant="secondary" onClick={() => handleOpenExpenseModal('INSURANCE')}>
            <Shield size={16} className="mr-2" />
            Add Insurance
          </Button>
        </div>
      </div>

      {/* Vehicle Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Truck size={24} className="text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Status</p>
              <Badge variant={vehicle.active ? 'success' : 'error'}>
                {vehicle.active ? 'Active' : 'Inactive'}
              </Badge>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-100 rounded-lg">
              <Fuel size={24} className="text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Fuel Type</p>
              <p className="font-semibold">{vehicle.fuelType}</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Gauge size={24} className="text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Current Mileage</p>
              <p className="font-semibold">{vehicle.currentMileage.toLocaleString()} km</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-orange-100 rounded-lg">
              <Calendar size={24} className="text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Expenses</p>
              <p className="font-semibold">{formatCurrency(totalExpenses)}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Vehicle Details */}
      <Card title="Vehicle Information">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-gray-500">Registration Number</p>
            <p className="font-medium">{vehicle.registrationNumber}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Make & Model</p>
            <p className="font-medium">{vehicle.make} {vehicle.model}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Year</p>
            <p className="font-medium">{vehicle.year}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Fuel Efficiency</p>
            <p className="font-medium">{vehicle.fuelEfficiencyKmPerLiter} km/L</p>
          </div>
          {vehicle.purchasingPrice && (
            <div>
              <p className="text-sm text-gray-500">Purchasing Price</p>
              <p className="font-medium">{formatCurrency(vehicle.purchasingPrice)}</p>
            </div>
          )}
        </div>
      </Card>

      {/* LATRA Expenses */}
      <Card title="LATRA Records">
        {latraExpenses.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Valid From</TableHead>
                  <TableHead>Valid Until</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Attachment</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {latraExpenses.map((expense) => (
                  <TableRow key={expense.id}>
                    <TableCell>{format(parseISO(expense.expenseDate), 'MMM dd, yyyy')}</TableCell>
                    <TableCell className="font-semibold">{formatCurrency(expense.amount)}</TableCell>
                    <TableCell>{expense.validFrom ? format(parseISO(expense.validFrom), 'MMM dd, yyyy') : '-'}</TableCell>
                    <TableCell>{expense.validUntil ? format(parseISO(expense.validUntil), 'MMM dd, yyyy') : '-'}</TableCell>
                    <TableCell>
                      <Badge variant={getStatusVariant(expense.status)}>{expense.status}</Badge>
                    </TableCell>
                    <TableCell>
                      {expense.hasAttachment ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setViewingAttachmentExpense(expense);
                              setIsAttachmentViewerOpen(true);
                            }}
                            className="flex items-center gap-1 text-primary hover:text-primary-dark"
                            title="View attachment"
                          >
                            {expense.attachmentContentType?.startsWith('image/') ? (
                              <ImageIcon size={14} />
                            ) : (
                              <Eye size={14} />
                            )}
                            <span className="text-sm">View</span>
                          </button>
                          <button
                            onClick={() => handleDownloadAttachment(expense)}
                            className="text-gray-500 hover:text-gray-700"
                            title="Download"
                          >
                            <Download size={14} />
                          </button>
                        </div>
                      ) : (
                        <span className="text-gray-400">No attachment</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">No LATRA records found</p>
        )}
      </Card>

      {/* Insurance Expenses */}
      <Card title="Insurance Records">
        {insuranceExpenses.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Provider</TableHead>
                  <TableHead>Policy #</TableHead>
                  <TableHead>Valid Until</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Attachment</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {insuranceExpenses.map((expense) => (
                  <TableRow key={expense.id}>
                    <TableCell>{format(parseISO(expense.expenseDate), 'MMM dd, yyyy')}</TableCell>
                    <TableCell className="font-semibold">{formatCurrency(expense.amount)}</TableCell>
                    <TableCell>{expense.insuranceProvider || '-'}</TableCell>
                    <TableCell>{expense.policyNumber || '-'}</TableCell>
                    <TableCell>{expense.validUntil ? format(parseISO(expense.validUntil), 'MMM dd, yyyy') : '-'}</TableCell>
                    <TableCell>
                      <Badge variant={getStatusVariant(expense.status)}>{expense.status}</Badge>
                    </TableCell>
                    <TableCell>
                      {expense.hasAttachment ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setViewingAttachmentExpense(expense);
                              setIsAttachmentViewerOpen(true);
                            }}
                            className="flex items-center gap-1 text-primary hover:text-primary-dark"
                            title="View attachment"
                          >
                            {expense.attachmentContentType?.startsWith('image/') ? (
                              <ImageIcon size={14} />
                            ) : (
                              <Eye size={14} />
                            )}
                            <span className="text-sm">View</span>
                          </button>
                          <button
                            onClick={() => handleDownloadAttachment(expense)}
                            className="text-gray-500 hover:text-gray-700"
                            title="Download"
                          >
                            <Download size={14} />
                          </button>
                        </div>
                      ) : (
                        <span className="text-gray-400">No attachment</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">No insurance records found</p>
        )}
      </Card>

      {/* Maintenance & Repair Expenses */}
      <Card title="Maintenance & Repair Records">
        {maintenanceExpenses.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Attachment</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {maintenanceExpenses.map((expense) => (
                  <TableRow key={expense.id}>
                    <TableCell>{format(parseISO(expense.expenseDate), 'MMM dd, yyyy')}</TableCell>
                    <TableCell>
                      <Badge variant="info">{getCategoryLabel(expense.category)}</Badge>
                    </TableCell>
                    <TableCell className="font-semibold">{formatCurrency(expense.amount)}</TableCell>
                    <TableCell className="max-w-xs truncate">{expense.description || '-'}</TableCell>
                    <TableCell>
                      <Badge variant={getStatusVariant(expense.status)}>{expense.status}</Badge>
                    </TableCell>
                    <TableCell>
                      {expense.hasAttachment ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setViewingAttachmentExpense(expense);
                              setIsAttachmentViewerOpen(true);
                            }}
                            className="flex items-center gap-1 text-primary hover:text-primary-dark"
                            title="View attachment"
                          >
                            {expense.attachmentContentType?.startsWith('image/') ? (
                              <ImageIcon size={14} />
                            ) : (
                              <Eye size={14} />
                            )}
                            <span className="text-sm">View</span>
                          </button>
                          <button
                            onClick={() => handleDownloadAttachment(expense)}
                            className="text-gray-500 hover:text-gray-700"
                            title="Download"
                          >
                            <Download size={14} />
                          </button>
                        </div>
                      ) : (
                        <span className="text-gray-400">No attachment</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">No maintenance or repair records found</p>
        )}
      </Card>

      {/* Other Expenses */}
      {otherExpenses.length > 0 && (
        <Card title="Other Expenses">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Attachment</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {otherExpenses.map((expense) => (
                  <TableRow key={expense.id}>
                    <TableCell>{format(parseISO(expense.expenseDate), 'MMM dd, yyyy')}</TableCell>
                    <TableCell>
                      <Badge variant="default">{getCategoryLabel(expense.category)}</Badge>
                    </TableCell>
                    <TableCell className="font-semibold">{formatCurrency(expense.amount)}</TableCell>
                    <TableCell className="max-w-xs truncate">{expense.description || '-'}</TableCell>
                    <TableCell>
                      <Badge variant={getStatusVariant(expense.status)}>{expense.status}</Badge>
                    </TableCell>
                    <TableCell>
                      {expense.hasAttachment ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setViewingAttachmentExpense(expense);
                              setIsAttachmentViewerOpen(true);
                            }}
                            className="flex items-center gap-1 text-primary hover:text-primary-dark"
                            title="View attachment"
                          >
                            {expense.attachmentContentType?.startsWith('image/') ? (
                              <ImageIcon size={14} />
                            ) : (
                              <Eye size={14} />
                            )}
                            <span className="text-sm">View</span>
                          </button>
                          <button
                            onClick={() => handleDownloadAttachment(expense)}
                            className="text-gray-500 hover:text-gray-700"
                            title="Download"
                          >
                            <Download size={14} />
                          </button>
                        </div>
                      ) : (
                        <span className="text-gray-400">No attachment</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {/* Add Expense Modal */}
      <Modal
        isOpen={isExpenseModalOpen}
        onClose={handleCloseExpenseModal}
        title={`Add ${expenseType} Expense`}
        footer={
          <>
            <Button variant="ghost" onClick={handleCloseExpenseModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmitExpense}
              loading={createExpenseMutation.isPending}
            >
              Record {expenseType}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input
            label="Amount (TZS)"
            type="number"
            value={expenseAmount}
            onChange={(e) => setExpenseAmount(e.target.value)}
            placeholder="e.g., 500000"
            required
          />
          <Input
            label="Payment Date"
            type="date"
            value={expenseDate}
            onChange={(e) => setExpenseDate(e.target.value)}
            required
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Valid From"
              type="date"
              value={validFrom}
              onChange={(e) => setValidFrom(e.target.value)}
              required
            />
            <Input
              label="Valid Until"
              type="date"
              value={validUntil}
              onChange={(e) => setValidUntil(e.target.value)}
              required
            />
          </div>
          {expenseType === 'INSURANCE' && (
            <>
              <Input
                label="Policy Number"
                value={policyNumber}
                onChange={(e) => setPolicyNumber(e.target.value)}
                placeholder="e.g., INS-2024-001"
              />
              <Input
                label="Insurance Provider"
                value={insuranceProvider}
                onChange={(e) => setInsuranceProvider(e.target.value)}
                placeholder="e.g., NIC Insurance"
              />
            </>
          )}
          <Input
            label="Description (Optional)"
            value={expenseDescription}
            onChange={(e) => setExpenseDescription(e.target.value)}
            placeholder={`${expenseType} payment description`}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Attachment (PDF, JPG, PNG)
            </label>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => setExpenseAttachment(e.target.files?.[0] || null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
            {expenseAttachment && (
              <div className="flex items-center gap-2 mt-1">
                <Paperclip size={14} className="text-gray-500" />
                <span className="text-xs text-gray-600">{expenseAttachment.name}</span>
                <button
                  type="button"
                  onClick={() => setExpenseAttachment(null)}
                  className="text-xs text-red-500 hover:text-red-700"
                >
                  Remove
                </button>
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Attachment Viewer */}
      {viewingAttachmentExpense && (
        <AttachmentViewer
          expenseId={viewingAttachmentExpense.id}
          fileName={viewingAttachmentExpense.attachmentFileName || 'attachment'}
          contentType={viewingAttachmentExpense.attachmentContentType || 'application/octet-stream'}
          isOpen={isAttachmentViewerOpen}
          onClose={() => {
            setIsAttachmentViewerOpen(false);
            setViewingAttachmentExpense(null);
          }}
          onDownload={() => handleDownloadAttachment(viewingAttachmentExpense)}
        />
      )}
    </div>
  );
};
